import Vue from 'vue'
import VueRouter from 'vue-router'
import MultiGuard from 'vue-router-multiguard'

//  Guards
import AuthGuard from '@/router/auth-guard'
import AuthGuardNot from '@/router/auth-guard-not'
// import IcpGuard from '@/router/icp-guard'
import invitationGuard from './invitation-guard'
import multiTenantGuard from './multi-tenant-guard'
import QboGuard from '@/router/qbo-guard'
import RegistrationGuard from '@/router/registration-guard'
import SigninGuard from '@/router/signin-guard'
import SubscriptionGuard from '@/router/subscription-guard'
import TenantGuard from '@/router/tenant-guard'
import TermsGuard from '@/router/terms-guard'
import TokenGuard from '@/router/token-guard'
import TwoFactorAuthGuard from '@/router/two-factor-auth-guard'
import XeroGuard from '@/router/xero-guard'

//  Important routes
import BroadcastsCreate from '@/views/BroadcastsCreate.vue'
import BroadcastsEdit from '@/views/BroadcastsEdit.vue'
import BroadcastsIndex from '@/views/BroadcastsIndex.vue'
import BroadcastsProfile from '@/views/BroadcastsProfile.vue'
import ContactsIndex from '@/views/ContactsIndex.vue'
import Inbox from '@/views/Inbox.vue'
import Teammates from '@/views/Teammates.vue'

Vue.use(VueRouter)

const routes = [
    {
        path: '/',
        redirect: '/signin',
        name: 'SignIn'
    },
    {
        path: '/brand-register',
        name: 'Brand',
        component: () => import(/* webpackChunkName: "brand-index" */ "@/views/BrandRegister.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/brand-info',
        name: 'BrandInfo',
        component: () => import(/* webpackChunkName: "brand-index" */ "@/views/BrandInfo.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/account',
        name: 'Account',
        component: () => import(/* webpackChunkName: "account-index" */ "@/views/Account.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/account/password-reset',
        name: 'AccountPasswordReset',
        component: () => import(/* webpackChunkName: "account-index" */ "@/views/AccountPasswordReset.vue"),
        beforeEnter: MultiGuard([SigninGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/auto-responders',
        name: 'AutoResponders',
        component: () => import(/* webpackChunkName: "autoresponders" */ "@/views/AutoResponders.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/billing',
        name: 'Billing',
        component:  () => import(/* webpackChunkName: "billing-index" */ "@/views/Billing.vue"),
        redirect: '/billing/subscription',
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard]),
        children: [
            {
                path: '/billing/subscription',
                name: 'BillingSubscription',
                component: () => import(/* webpackChunkName: "billing-index" */ "@/views/BillingSubscription.vue"),
                meta: { permission: 1, feature: 'subscription_handling' }
            },
            {
                path: '/billing/plan',
                name: 'BillingSubscriptionForm',
                component: () => import(/* webpackChunkName: "billing-index" */ "@/views/BillingSubscriptionForm.vue"),
                meta: { permission: 1, feature: 'subscription_handling' }
            },
            {
                path: '/billing/manage',
                name: 'BillingManage',
                component: () => import(/* webpackChunkName: "billing-index" */ "@/views/BillingManage.vue"),
                meta: { permission: 1, feature: 'subscription_handling' }
            }
        ]
    },
    {
        path: '/broadcasts',
        name: 'BroadcastsIndex',
        component: BroadcastsIndex,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/broadcasts/create',
        name: 'BroadcastsCreate',
        component: BroadcastsCreate,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/broadcasts/:id',
        name: 'BroadcastProfile',
        component: BroadcastsProfile,
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/broadcasts/:id/edit',
        name: 'BroadcastsEdit',
        component: BroadcastsEdit,
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/chatbots',
        name: 'ChatbotsIndex',
        component: () => import(/* webpackChunkName: "chatbots-forms" */ "@/views/ChatBotsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/chatbot/:id',
        name: 'ChatbotProfile',
        props: true,
        component: () => import(/* webpackChunkName: "chatbots-forms" */ "@/views/ChatBotProfile.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/contacts',
        name: 'ContactsIndex',
        component: ContactsIndex,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/csv/import',
        name: 'CsvImport',
        component: () => import(/* webpackChunkName: "csv-import" */ "@/views/CsvImport.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/free-credits',
        name: 'FreeCredits',
        component: () => import(/* webpackChunkName: "free-credits" */ "@/views/FreeCredits.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/groups',
        name: 'GroupsIndex',
        component: () => import(/* webpackChunkName: "groups-index" */ "@/views/GroupsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/groups/create',
        name: 'GroupsCreate',
        component: () => import(/* webpackChunkName: "groups-index" */ "@/views/GroupCreate.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/groups/:id',
        name: 'GroupProfile',
        component: () => import(/* webpackChunkName: "groups-index" */ "@/views/GroupProfile.vue"),
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/groups/:id/edit',
        name: 'GroupsEdit',
        component: () => import(/* webpackChunkName: "groups-index" */ "@/views/GroupEdit.vue"),
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/home',
        name: 'Home',
        component: () => import(/* webpackChunkName: "home-dashboard" */ "@/views/HomeDashboard.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, SigninGuard, TenantGuard, AuthGuard, TermsGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import(/* webpackChunkName: "forgot-password" */ "@/views/ForgotPassword.vue"),
        beforeEnter: AuthGuardNot,
        meta: { permission: 0 }
    },
    {
        path: '/forms',
        name: 'FormsIndex',
        component: () => import(/* webpackChunkName: "chatbots-forms" */ "@/views/FormsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/forms/:id',
        name: 'FormsProfile',
        component: () => import(/* webpackChunkName: "chatbots-forms" */ "@/views/FormsProfile.vue"),
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/inbox',
        name: 'Inbox',
        component:  Inbox,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, SigninGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard])
    },
    {
        path: '/i/g/:groupId',
        name: 'Inbox Group',
        component: Inbox,
        props: true,
        beforeEnter: MultiGuard([SigninGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]) // TODO: Do I need to add 2FA guard here?
    },
    {
        path: '/integrations',
        name: 'Integrations',
        component: () => import(/* webpackChunkName: "integrations" */ "@/views/Integrations.vue"),
        beforeEnter: MultiGuard([SigninGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]) // TODO: Do I need to add 2FA guard here?
    },
    {
        path: '/invitations/accept',
        name: 'InvitationsAccept',
        component: () => import(/* webpackChunkName: "invitations-accept" */ "@/views/InvitationsAccept.vue"),
        beforeEnter: MultiGuard([invitationGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/invoices',
        name: 'InvoicesIndex',
        component: () => import(/* webpackChunkName: "invoices-index" */ "@/views/InvoicesIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
    },
    {
        path: '/media',
        name: 'MediaIndex',
        component: () => import(/* webpackChunkName: "media-index" */ "@/views/MediaIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/media/folder/:folder',
        name: 'MediaFolder',
        component: () => import(/* webpackChunkName: "media-index" */ "@/views/MediaFolder.vue"),
        props: true,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/memberships',
        name: 'Memberships',
        component: () => import(/* webpackChunkName: "memberships" */ "@/views/Memberships.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, SigninGuard, AuthGuard, multiTenantGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/messages',
        name: 'Messages',
        component: () => import(/* webpackChunkName: "messages-feed" */ "@/views/MessagesFeed.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/migration',
        name: 'Migration',
        component: () => import(/* webpackChunkName: "migration" */ "@/views/Migration.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/signature',
        name: 'MessageSignature',
        component: () => import(/* webpackChunkName: "messages-signature" */ "@/views/MessageSignature.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/notifications',
        name: 'NotificationsIndex',
        component: () => import(/* webpackChunkName: "notifications-index" */ "@/views/NotificationsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
    },
    {
        path: '/office-hours',
        name: 'OfficeHours',
        component: () => import(/* webpackChunkName: "office-hours" */ "@/views/OfficeHours.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/opt',
        name: 'Opt',
        component: () => import(/* webpackChunkName: "opt" */ "@/views/Opt.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/payments',
        name: 'Payments',
        component: () => import(/* webpackChunkName: "invoice-campaigns" */ "@/views/InvoiceCampaigns.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard])
    },
    {
        path: '/persons/search',
        name: 'PersonsSearch',
        component: () => import(/* webpackChunkName: "persons-search" */ "@/views/PersonsSearch.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard])
    },
    {
        path: '/plugins',
        name: 'PluginsIndex',
        component: () => import(/* webpackChunkName: "plugins-index" */ "@/views/PluginsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard])
    },
    {
        path: '/qbo',
        name: 'Qbo',
        component: () => import(/* webpackChunkName: "qbo" */ "@/views/Qbo.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, SigninGuard, TenantGuard, AuthGuard, TermsGuard, QboGuard]),
        meta: { permission: 2, condition: '$tenant.data_provider==="QBO"||$tenant.data_provider===null' }
    },
    {
        path: '/qbo/link-account',
        name: 'QboLinkAccount',
        component: () => import(/* webpackChunkName: "qbo" */ "@/views/QboLinkAccount.vue"),
        beforeEnter: TokenGuard,
        meta: { permission: 0 }
    },
    {
        path: '/referral',
        name: 'Referral',
        component: () => import(/* webpackChunkName: "referrals" */ "@/views/Referral.vue"),
        beforeEnter: MultiGuard([SigninGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard])
    },
    {
        path: '/register',
        redirect: '/register/user',
        name: 'RegisterUser'
    },
    {
        path: '/register/authenticated',
        name: 'RegisterAuthenticated',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterAuthenticated.vue"),
        beforeEnter: MultiGuard([SigninGuard, AuthGuard, RegistrationGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/register/building',
        name: 'RegisterBuilding',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterBuilding.vue"),
        beforeEnter: MultiGuard([AuthGuard, RegistrationGuard]),
        meta: { permission: 0 }
    },
    // {
    //     path: '/register/icp',
    //     name: 'RegisterIcp',
    //     component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterIcp.vue"),
    //     beforeEnter: MultiGuard([SigninGuard, AuthGuard, RegistrationGuard])
    // },
    {
        path: '/register/payment',
        name: 'RegisterPayment',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterPayment.vue"),
        beforeEnter: MultiGuard([SigninGuard, AuthGuard, RegistrationGuard]),
    },
    // {
    //     path: '/register/plan',
    //     name: 'RegisterPlan',
    //     component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterPlan.vue"),
    //     beforeEnter: MultiGuard([SigninGuard, AuthGuard, RegistrationGuard])
    // },
    {
        path: '/register/user',
        name: 'RegisterUser',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterUser.vue"),
        beforeEnter: AuthGuardNot,
        meta: { permission: 0 }
    },
    {
        path: '/register/verify',
        name: 'RegisterVerify',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterVerify.vue"),
        beforeEnter: MultiGuard([SigninGuard, AuthGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/register/verifysms',
        name: 'RegisterVerifySms',
        component: () => import(/* webpackChunkName: "register" */ "@/views/RegisterVerifySms.vue"),
        beforeEnter: MultiGuard([AuthGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/custom-fields',
        name: 'CustomFields',
        component: () => import(/* webpackChunkName: "custom-fields" */ "@/views/CustomFields.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/settings',
        name: 'SettingsIndex',
        component: () => import(/* webpackChunkName: "settings-index" */ "@/views/SettingsIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/settings/merge-contacts',
        name: 'PersonsMerge',
        component: () => import(/* webpackChunkName: "persons-merge" */ "@/views/PersonsMerge.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/short-links',
        name: 'ShortLinks',
        component: () => import(/* webpackChunkName: "short-links" */ "@/views/ShortLinks.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/signin',
        name: 'SignIn',
        component: () => import(/* webpackChunkName: "signin" */ "@/views/SignIn.vue"),
        beforeEnter: AuthGuardNot,
        meta: { permission: 0 }
    },
    {
        path: '/signin/2fa',
        name: 'TwoFactorAuthentication',
        component: () => import(/* webpackChunkName: "signin" */ "@/views/TwoFactorAuthentication.vue"),
        beforeEnter: MultiGuard([AuthGuard]),
        meta: { permission: 0 }
    },
    {
        path: '/sphere',
        name: 'Sphere',
        component: () => import(/* webpackChunkName: "sphere" */ "@/views/Sphere.vue"),
        beforeEnter: MultiGuard([TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/teammates',
        name: 'Teammates',
        component: Teammates,
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/templates',
        name: 'TemplatesIndex',
        component: () => import(/* webpackChunkName: "templates-index" */ "@/views/TemplatesIndex.vue"),
        beforeEnter: MultiGuard([TwoFactorAuthGuard, TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard]),
        meta: { permission: 1 }
    },
    {
        path: '/xero',
        name: 'Xero',
        component: () => import(/* webpackChunkName: "xero" */ "@/views/Xero.vue"),
        beforeEnter: MultiGuard([TenantGuard, AuthGuard, TermsGuard, SubscriptionGuard, XeroGuard]),
        meta: { permission: 2 }
    },
    {
        path: '/xero/link-account',
        name: 'XeroLinkAccount',
        component: () => import(/* webpackChunkName: "xero" */ "@/views/XeroLinkAccount.vue"),
        beforeEnter: TokenGuard,
        meta: { permission: 0 }
    }
]

const router = new VueRouter({
    mode: 'hash',
    base: process.env.BASE_URL,
    routes
})

export default router
